import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useCall } from '../../../context/CallContext';
import api from '../../../utils/api';

const ProviderSession = () => {
  const { user } = useAuth();
  const { startCall } = useCall();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, active, completed

  useEffect(() => {
    fetchConsultations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/consultation/history/${user?._id || user?.id}`);
      const data = response.data;
      if (data.success) {
        const list = data.data?.items ?? data.data;
        let filtered = Array.isArray(list) ? list : [];
        if (filter === 'pending') {
          filtered = filtered.filter(c => c.status === 'pending');
        } else if (filter === 'active') {
          filtered = filtered.filter(c => c.status === 'active');
        } else if (filter === 'completed') {
          filtered = filtered.filter(c => c.status === 'completed');
        }
        setConsultations(filtered);
      } else {
        showError(data.message || 'Failed to fetch sessions');
      }
    } catch (error) {
      console.error('Error fetching consultations:', error);
      showError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const handleAcceptClick = (consultation) => {
    if (processingId) return; // Prevent double click
    if (consultation.consultation_type === 'chat') {
      acceptConsultation(consultation._id || consultation.id);
    } else {
      setSelectedConsultation(consultation);
      setShowScheduleModal(true);
    }
  };

  const acceptConsultation = async (consultationId, scheduledAt = null) => {
    try {
      setProcessingId(consultationId);
      const body = scheduledAt ? { scheduledAt } : {};
      const response = await api.post(`/consultation/${consultationId}/accept`, body);
      const data = response.data;
      if (data.success) {
        showSuccess('Session accepted successfully');
        setShowScheduleModal(false);
        setScheduleTime('');
        setSelectedConsultation(null);
        fetchConsultations();
      } else {
        showError(data.message || 'Failed to accept session');
      }
    } catch (error) {
      showError('Failed to accept session');
    } finally {
      setProcessingId(null);
    }
  };


  const startConsultation = async (consultationId, consultationObj = null) => {
    try {
      const response = await api.put(`/consultation/start/${consultationId}`);
      const data = response.data;
      if (data.success) {
        showSuccess('Session started');

        // Ring the patient via sockets
        if (consultationObj) {
          const patientId = consultationObj.patient?._id || consultationObj.patient_id?._id || consultationObj.patient_id || consultationObj.patient;
          if (patientId) {
            startCall(consultationId, patientId, consultationObj.consultation_type);
          }
        }

        // Auto-navigate doctor to the room so they are waiting when patient accepts
        navigate(`/consultation/${consultationId}`);
        fetchConsultations();
      } else {
        showError(data.message || 'Failed to start session');
      }
    } catch (error) {
      showError('Failed to start session');
    }
  };

  const completeConsultation = async (consultationId) => {
    try {
      const response = await api.put(`/consultation/complete/${consultationId}`);
      const data = response.data;
      if (data.success) {
        showSuccess('Session completed');
        fetchConsultations();
      } else {
        showError(data.message || 'Failed to complete session');
      }
    } catch (error) {
      showError('Failed to complete session');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My Sessions</h1>

      {/* Filter Tabs */}
      <div className="flex space-x-2 bg-gray-800 rounded-lg p-1">
        {['pending', 'scheduled', 'active', 'completed'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${filter === tab
              ? 'bg-gold text-dark'
              : 'text-gray-400 hover:text-light'
              }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Consultations List */}
      <div className="space-y-4">
        {consultations.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl">
            <p className="text-gray-400">No {filter} sessions</p>
          </div>
        ) : (
          consultations.map((consultation) => (
            <div
              key={consultation.id || consultation._id}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-light">
                    {consultation.patient?.name || 'User'}
                  </h3>
                  <p className="text-sm text-gold capitalize">
                    {consultation.consultation_type || 'N/A'} - {consultation.reason_for_visit || 'General Session'}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${consultation.status === 'requested' ? 'bg-yellow-900 text-yellow-300' :
                  consultation.status === 'scheduled' ? 'bg-blue-900 text-blue-300' :
                    consultation.status === 'active' || consultation.status === 'in_progress' ? 'bg-green-900 text-green-300' :
                      'bg-gray-700 text-gray-300'
                  }`}>
                  {consultation.status === 'requested' ? 'Pending Request' : consultation.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Date:</span>
                  <span className="text-light">
                    {new Date(consultation.created_at || consultation.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {consultation.scheduledAt && (
                  <div className="flex justify-between text-sm bg-gray-700 p-2 rounded">
                    <span className="text-gold font-bold">Scheduled For:</span>
                    <span className="text-light font-bold">
                      {new Date(consultation.scheduledAt).toLocaleString()}
                    </span>
                  </div>
                )}
                {consultation.fee && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Fee:</span>
                    <span className="text-gold font-medium">₹{consultation.fee}</span>
                  </div>
                )}
              </div>

              {consultation.status === 'requested' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAcceptClick(consultation)}
                    disabled={processingId === (consultation._id || consultation.id)}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingId === (consultation._id || consultation.id) ? 'Accepting...' : 'Accept Request'}
                  </button>
                </div>
              )}

              {(consultation.status === 'scheduled' || consultation.status === 'accepted') && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      if (consultation.consultation_type === 'chat') {
                        navigate(`/chat/${consultation.id || consultation._id}`);
                      } else {
                        startConsultation(consultation.id || consultation._id, consultation);
                      }
                    }}
                    className="flex-1 bg-gold text-dark py-2 rounded-lg font-medium"
                  >
                    {consultation.consultation_type === 'chat' ? 'Open Chat' : 'Start Call Now'}
                  </button>
                </div>
              )}

              {(consultation.status === 'active' || consultation.status === 'in_progress') && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      if (consultation.consultation_type === 'chat') {
                        navigate(`/chat/${consultation.id || consultation._id}`);
                      } else {
                        navigate(`/consultation/${consultation.id || consultation._id}`);
                      }
                    }}
                    className="flex-1 bg-gray-700 text-light py-2 rounded-lg font-medium mr-2"
                  >
                    {consultation.consultation_type === 'chat' ? 'Open Chat' : 'Join Room'}
                  </button>
                  <button
                    onClick={() => navigate(`/prescription/create/${consultation.id || consultation._id}`)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-medium"
                  >
                    Recommend
                  </button>
                  <button
                    onClick={() => completeConsultation(consultation.id || consultation._id)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
                  >
                    Complete
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gold mb-4">Schedule Consultation</h3>
            <p className="text-gray-300 mb-4">
              Select a time for this {selectedConsultation?.consultation_type} session.
            </p>

            <input
              type="datetime-local"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light mb-6"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 bg-gray-700 text-light py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => acceptConsultation(selectedConsultation._id || selectedConsultation.id, scheduleTime)}
                disabled={!scheduleTime}
                className="flex-1 bg-gold text-dark py-2 rounded-lg font-medium disabled:opacity-50"
              >
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderSession;
